<script setup lang="ts">
import { Image } from '@/components';
import { type FabricStore, getStyle3dFabricApi } from '@/api';
import { useRoute, useRouter } from 'vue-router';
import { useParamStore } from '@/store/param.ts';
import { useFabricStore } from '@/store/fabric.ts';
import { storeToRefs, useRelationStore, useSelectionStore, useStyle3dStore } from '@repo/stores';
import { type FabricShareInfo, useStyle3d } from '@repo/style3d';
import { joinUrl } from '@repo/utils';
import axios, { type AxiosResponse } from 'axios';
import { generateFabricInfo, getAppConfig } from '@/utils';
import { showConfirmDialog } from 'vant';
import { assignNewMaterialIndex } from '@/utils/materialOverride';
import { $t } from '@/locales';

const props = defineProps<{ fabrics: FabricStore[] }>();

const router = useRouter();
const route = useRoute();
const { refreshLoadResourceByRoute } = useParamStore();

const selectionStore = useSelectionStore();
const relationStore = useRelationStore();
const paramStore = useParamStore();
const fabricStore = useFabricStore();

const style3dStore = useStyle3dStore();
const { resolution } = storeToRefs(style3dStore);

const { OSS_BASE_URL, INDIVIDUAL_FABRIC_SHARES } = getAppConfig();
const fabricRootPath = `${OSS_BASE_URL}${INDIVIDUAL_FABRIC_SHARES}`;

const { loadFabric } = useStyle3d();

/**
 * 执行面料替换的核心逻辑（对指定的 shapeList 执行）
 */
const doReplaceFabric = async (
  item: FabricStore,
  fabricId: string,
  originUrl: string,
  targetShapes: ReturnType<typeof relationStore.shapeList.filter>,
  materialIndex: number,
  fabricShareInfo: Omit<FabricShareInfo, 'mask' | 'metaData' | 'doc' | 'enterprise_id'>
) => {
  const updatedList = targetShapes.map(shape => {
    const fabricInfoType = shape.type ? shape.type : 'main';
    return {
      ...shape,
      ...fabricShareInfo.info[fabricInfoType] ?? fabricShareInfo.info['main'],
      color: '' // 清空变色的颜色
    };
  });

  const fabric = {
    id: item.id,
    type: item.resourceType,
    originUrl: item.originUrl
  };

  if (item.resourceType === 'style3d') {
    const shapes = await loadFabric(updatedList, { fabricRootPath: '', resolution: resolution.value, fabric });
    relationStore.updateShape(shapes);
  } else {
    const fabricRoot = joinUrl(fabricRootPath, fabricId);
    const shapes = await loadFabric(updatedList, {
      fabricRootPath: fabricRoot,
      resolution: resolution.value,
      fabric
    });
    relationStore.updateShape(shapes);
  }

  // 更新 fabricStore 中对应 materialIndex 的面料信息
  try {
    let fullFabricInfo;
    if (item.resourceType === 'style3d') {
      const style3dFabric = await getStyle3dFabricApi(fabricId);
      const xhr_path = style3dFabric.data.xhr_path;
      const thumb = style3dFabric.data.thumb_path;

      const keyMap = style3dFabric.create_info.fields.map(field => ({ name: field.name, key: field.key }));
      const fabricProperty = keyMap
        .map((field) => ({ name: field.name, key: field.key, value: style3dFabric.data[field.key] }))
        .filter(field => field.value);

      const carousels = style3dFabric.data?.all_thumbs?.map((thumbItem: any) => {
        const url = thumbItem.thumb_path.split('?')[0];
        const urlParts = url.split('/');
        const decodedFileName = decodeURIComponent(urlParts[urlParts.length - 1]);
        const fileType = decodedFileName?.split('.')?.pop()?.toLowerCase() || '';
        const fileCategory = ['jpg', 'jpeg', 'png', 'gif', 'bmp'].includes(fileType) ? 'picture' :
          ['mp4', 'avi', 'mov', 'wmv'].includes(fileType) ? 'video' : 'unknown';
        return { name: decodedFileName, type: fileCategory, url };
      });

      fullFabricInfo = {
        id: `style3d-${fabricId}`,
        originUrl: item.originUrl || '',
        fabricId: fabricId,
        info: { name: xhr_path, width: style3dFabric.data.width, height: style3dFabric.data.height },
        carousels,
        resourceType: 'style3d' as const,
        enterpriseId: item.enterpriseId || '',
        enterpriseName: item.enterpriseName || '',
        thumb,
        xhr_path,
        properties: fabricProperty,
        mask: { srcImage: undefined, colors: undefined, list: [] }
      };
    } else {
      const fabricRoot = joinUrl(fabricRootPath, fabricId);
      const infoResult: AxiosResponse<FabricShareInfo> = await axios.get(joinUrl(fabricRoot, 'info.json'));
      const fullFabricShareInfo = infoResult.data;
      const thumbUrl = fullFabricShareInfo.info?.thumbnail
        ? joinUrl(fabricRoot, 'fabric', fullFabricShareInfo.info.thumbnail.name)
        : fullFabricShareInfo.info.main
          ? joinUrl(fabricRoot, 'fabric', fullFabricShareInfo.info.main.name)
          : item.thumb;

      fullFabricInfo = {
        id: item.id,
        originUrl: item.originUrl || '',
        fabricId: fabricId,
        info: fullFabricShareInfo.metaData.info,
        thumb: thumbUrl,
        xhr_path: undefined,
        enterpriseId: fullFabricShareInfo.metaData.enterpriseID || item.enterpriseId || '',
        enterpriseName: fullFabricShareInfo.metaData.enterpriseName || item.enterpriseName || '',
        resourceType: 'cloudknit' as const,
        properties: fullFabricShareInfo.metaData.property || item.properties || [],
        styleProperties: fullFabricShareInfo.metaData?.propertyStyle,
        carousels: fullFabricShareInfo.doc?.map(docItem => ({ ...docItem, url: joinUrl(fabricRoot, docItem.name) })) || [],
        logo: fullFabricShareInfo.metaData.logo,
        mask: {
          srcImage: fullFabricShareInfo.mask?.srcImage,
          colors: fullFabricShareInfo.mask?.colors,
          list: fullFabricShareInfo.mask?.list || []
        }
      };
    }

    await fabricStore.setFabricInfo(fullFabricInfo, materialIndex);

    const currentSelectedIndex = fabricStore.getSelectedRegionIndex(materialIndex);
    const newRegionCount = fullFabricInfo.mask?.colors?.length || 0;
    if (newRegionCount > 0 && currentSelectedIndex >= newRegionCount) {
      fabricStore.setSelectedRegionIndex(0, materialIndex);
    }
  } catch (error) {
    console.error('[fabric-list] 更新fabricStore失败:', error);
  }
};

const onClick = async (item: FabricStore) => {
  const { selectedFabricId } = selectionStore;
  const { query, params } = route;
  const { scoResource } = paramStore.resourceParam;
  // 兼容老数据，之前只存了id没有存fabricId
  const fabricId = item.fabricId || item.id;
  const originUrl = item.originUrl || '';

  console.log('[fabric-list] onClick:', {
    fabricId,
    selectedFabricId,
    currentParams: params,
    currentQuery: query
  });

  if (selectedFabricId === '' || selectedFabricId === null) {
    // 没有选中版片，直接替换整个面料（原有逻辑）
    await router.replace({
      params: { type: item.resourceType, id: fabricId },
      query: { ...query, originUrl: btoa(originUrl), color: undefined, colors: undefined }
    });
    await refreshLoadResourceByRoute();
    return;
  }

  // 有选中版片，获取面料信息
  let fabricShareInfo: Omit<FabricShareInfo, 'mask' | 'metaData' | 'doc' | 'enterprise_id'>;
  if (item.resourceType === 'style3d') {
    const style3dFabric = await getStyle3dFabricApi(fabricId);
    const { xhr_path, thumb_path, width, height } = style3dFabric.data;
    const generateRibInfo = scoResource.type !== 'style3d';
    fabricShareInfo = await generateFabricInfo(
      { info: { width, height }, thumb: thumb_path, xhr_path: xhr_path },
      generateRibInfo
    );
  } else {
    const fabricRoot = joinUrl(fabricRootPath, fabricId);
    const infoResult: AxiosResponse<FabricShareInfo> = await axios.get(joinUrl(fabricRoot, 'info.json'));
    fabricShareInfo = infoResult.data;
  }

  const found = relationStore.shapeList.find(shape => shape.id === selectedFabricId);
  const materialIndex = found?.materialIndex ?? 0;

  // 找出所有共享同一 materialIndex 的 main 版片
  const sameMaterialShapes = relationStore.shapeList.filter(shape => {
    return (shape.materialIndex ?? 0) === materialIndex;
  });

  console.log('[fabric-list] 更新面料:', {
    selectedFabricId,
    materialIndex,
    fabricId,
    foundShape: found,
    sameMaterialCount: sameMaterialShapes.length
  });

  // 构建路由更新参数
  const currentParams = params as any;
  const newParams = { ...currentParams };
  const newQuery: any = { ...query, originUrl: btoa(originUrl) };

  // 清空对应 materialIndex 的颜色
  if (query.colors) {
    try {
      const colorsObj = JSON.parse(decodeURIComponent(query.colors as string));
      delete colorsObj[materialIndex];
      if (Object.keys(colorsObj).length > 0) {
        newQuery.colors = encodeURIComponent(JSON.stringify(colorsObj));
      } else {
        newQuery.colors = undefined;
      }
    } catch (e) {
      console.error('[fabric-list] 解析colors参数失败:', e);
      newQuery.colors = undefined;
    }
  }
  if (materialIndex === 0 && query.color) {
    newQuery.color = undefined;
  }

  // 判断是否有其他版片共享同一面料，决定是否需要询问
  const otherShapes = sameMaterialShapes.filter(s => s.id !== selectedFabricId);
  let syncMode: 'all' | 'current' = 'all';

  if (otherShapes.length > 0) {
    try {
      await showConfirmDialog({
        title: $t('sync.title'),
        message: $t('sync.message', { count: otherShapes.length }),
        confirmButtonText: $t('sync.confirm'),
        cancelButtonText: $t('sync.cancel'),
      });
      syncMode = 'all';
    } catch {
      syncMode = 'current';
    }
  }

  if (syncMode === 'all') {
    // 同步替换所有共享该 materialIndex 的版片
    await doReplaceFabric(item, fabricId, originUrl, sameMaterialShapes, materialIndex, fabricShareInfo);

    // 更新路由参数中对应 materialIndex 的面料 ID
    if (materialIndex === 0) {
      newParams.id = fabricId;
      newParams.type = item.resourceType;
    } else if (materialIndex === 1) {
      newParams.id1 = fabricId;
    } else if (materialIndex === 2) {
      newParams.id2 = fabricId;
    }

    await router.replace({ params: newParams, query: newQuery });
  } else {
    // 只替换当前版片，分配新 materialIndex
    const currentPiece = found?.piece ?? '';
    const newIndex = await assignNewMaterialIndex(
      currentPiece,
      materialIndex,
      relationStore.shapeList,
      router,
      fabricId
    );

    if (newIndex === null) {
      // 已达上限，降级为只替换当前版片但不改 materialIndex
      console.warn('[fabric-list] 已达 materialIndex 上限，直接替换当前版片');
      await doReplaceFabric(item, fabricId, originUrl, [found!].filter(Boolean), materialIndex, fabricShareInfo);
      await router.replace({ params: newParams, query: newQuery });
      return;
    }

    // 更新 store 中该版片的 materialIndex
    if (found) {
      relationStore.updateShape([{ ...found, materialIndex: newIndex }]);
    }

    // 执行面料替换（用新 materialIndex）
    const currentShape = relationStore.shapeList.find(s => s.id === selectedFabricId);
    if (currentShape) {
      await doReplaceFabric(item, fabricId, originUrl, [currentShape], newIndex, fabricShareInfo);
    }

    // assignNewMaterialIndex 已经更新了路由（写入新 id 参数和 overrides），
    // 这里只需额外清空颜色参数（assignNewMaterialIndex 内部已处理 colors 复制，
    // 但换面料时应清空颜色）
    const latestQuery = { ...router.currentRoute.value.query } as any;
    if (latestQuery.colors) {
      try {
        const colorsObj = JSON.parse(decodeURIComponent(latestQuery.colors));
        delete colorsObj[newIndex];
        if (Object.keys(colorsObj).length > 0) {
          latestQuery.colors = encodeURIComponent(JSON.stringify(colorsObj));
        } else {
          latestQuery.colors = undefined;
        }
      } catch {
        latestQuery.colors = undefined;
      }
    }
    latestQuery.originUrl = btoa(originUrl);
    await router.replace({ params: router.currentRoute.value.params, query: latestQuery });
  }
};
</script>

<template>
  <div
    style="padding: 0 5px;"
    v-for="(item) in props.fabrics"
    :key="item.id"
  >
    <Image
      :width="85"
      :height="85"
      :src="item.thumb"
      fit="scale-down"
      @click="()=>onClick(item)"
    />
    <div class="fabric-name">
      {{ item.info ? item.info.name : '' }}
    </div>
  </div>
</template>

<style scoped>
.fabric-name {
  color: rgba(255, 255, 255, 0.92);
  font-size: 12px;
  text-align: center;
  margin-top: 6px;
  line-height: 1.2;
  max-height: 3.6em; /* allow up to 3 lines */
  width: 85px; /* match image width */
  margin-left: auto;
  margin-right: auto;
  overflow: hidden;
  display: -webkit-box;
  line-clamp: 3;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  word-break: break-word;
  text-overflow: ellipsis;
  white-space: normal;
}
</style>